import { Card, Flex, Table, Typography } from "antd";
import type { ColumnType } from "antd/lib/table";
import { ImpressaoContainer, ImpressaoHeader } from "components/Impressao";
import type { ApiCalculo } from "pages/calculo/types/ApiCalculo";
import type { ApiCalculoDesconto } from "pages/calculo/types/ApiCalculoDesconto";
import { decimalFormater, numberToCurrency } from "std/format";
import { roundAbnt } from "std/math";

const { Title } = Typography;

export const columns: ColumnType<ApiCalculo>[] = [
	{
		dataIndex: "calc_nfe_numero_doc",
		key: "calc_nfe_numero_doc",
		title: "NFe",
		render: (calc_nfe_numero_doc, record) => (
			<>
				{calc_nfe_numero_doc
					? `${calc_nfe_numero_doc}/${record?.calc_nfe_serie_doc}`
					: record?.calc_nfe_chave
						? ` Chave Fiscal: ${record?.calc_nfe_chave}`
						: ""}
			</>
		),
	},
	{
		dataIndex: "calc_data_faturamento",
		key: "calc_data_faturamento",
		title: "Emissão",
	},
	{
		dataIndex: ["produtor", "for_codigoindividual"],
		key: "for_codigoindividual",
		title: "Cliente",
		render: (for_codigoindividual, record) => (
			<>
				{for_codigoindividual || ""} - {record?.produtor?.pessoa?.pes_nome_razaosocial || ""}
			</>
		),
	},
	{
		dataIndex: "calc_volume",
		key: "calc_volume",
		title: "Volume",
		align: "right",
		render: (value) => decimalFormater(value || 0),
	},
	{
		dataIndex: "calc_valor",
		key: "calc_valor",
		title: "Total NFe",
		align: "right",
		render: (value) => numberToCurrency(value || 0),
	},
	{
		dataIndex: "descontos",
		key: "fundesa",
		title: "Fundesa",
		align: "right",
		render: (descontos: ApiCalculoDesconto[]) => {
			const findFund = descontos.find((desc) => desc.calc_desc_tipo === 4);

			return numberToCurrency(findFund?.calc_desc_valor || 0);
		},
	},
	{
		dataIndex: "descontos",
		key: "funrural",
		title: "Funrural",
		align: "right",
		render: (descontos: ApiCalculoDesconto[]) => {
			const FindFunrural = descontos.find((desc) => desc.calc_desc_tipo === 3);

			return numberToCurrency(FindFunrural?.calc_desc_valor || 0);
		},
	},
	{
		dataIndex: "calc_senar",
		key: "calc_senar",
		title: "Senar",
		align: "right",
		render: (value) => numberToCurrency(value || 0),
	},
	{
		dataIndex: "calc_valor_total",
		key: "calc_valor_total",
		title: "Total",
		align: "right",
		render: (value) => numberToCurrency(value || 0),
	},
];

export function TableRelFunrural({ data, title }: { data: ApiCalculo[] | undefined; title: string }) {
	const totalVolume = roundAbnt((data || []).reduce((acc, cur) => acc + cur.calc_volume, 0));

	const totalNfe = roundAbnt((data || []).reduce((acc, cur) => acc + cur.calc_valor, 0));
	const totalFundesa = roundAbnt((data || []).reduce((acc, cur) => acc + cur.calc_fundesa, 0));
	const totalFunrural = roundAbnt((data || []).reduce((acc, cur) => acc + cur.calc_funrural, 0));
	const totalSenar = roundAbnt((data || []).reduce((acc, cur) => acc + cur.calc_senar, 0));
	const totalSaldo = roundAbnt((data || []).reduce((acc, cur) => acc + cur.calc_valor_total, 0));

	return (
		<ImpressaoContainer className="w-full mt-3" title={title}>
			<ImpressaoHeader title={title} />

			<Table
				rowKey="calc_idpk"
				columns={columns}
				dataSource={data}
				pagination={false}
				size="small"
				rowClassName="zebra"
				summary={(rows) => (
					<Table.Summary.Row>
						<Table.Summary.Cell index={0} colSpan={2} align="left" className={"antd-table-td-top"}>
							<div style={{ display: "flex", height: "100%", marginTop: "8px" }}>
								<Flex vertical={true}>
									<Title level={5}>Registros:</Title>
									<Title level={5} style={{ margin: "0px", textAlign: "right" }}>
										{rows?.length || 0}
									</Title>
								</Flex>
							</div>
						</Table.Summary.Cell>

						<Table.Summary.Cell index={1} colSpan={7} align="right">
							<Flex className="w-full mt-3" justify={"end"}>
								<Card
									title={
										<Flex className="w-full" align="center" justify="center">
											Totais
										</Flex>
									}
									bordered={false}
									size={"small"}
									style={{ width: 300 }}
								>
									<Flex
										className="w-full"
										justify={"space-between"}
										style={{
											fontWeight: "bold",
											fontFamily: "Mukta",
										}}
									>
										Volume: <b>{decimalFormater(totalVolume)}</b>
									</Flex>
									<Flex
										className="w-full"
										justify={"space-between"}
										style={{
											fontWeight: "bold",
											fontFamily: "Mukta",
										}}
									>
										Total Nfe: <b>{numberToCurrency(totalNfe)}</b>
									</Flex>
									<Flex
										className="w-full"
										justify={"space-between"}
										style={{
											fontWeight: "bold",
											fontFamily: "Mukta",
										}}
									>
										Fundesa: <b>{numberToCurrency(totalFundesa, 4)}</b>
									</Flex>
									<Flex
										className="w-full"
										justify={"space-between"}
										style={{
											fontWeight: "bold",
											fontFamily: "Mukta",
										}}
									>
										Funrural: <b>{numberToCurrency(totalFunrural, 4)}</b>
									</Flex>
									<Flex
										className="w-full"
										justify={"space-between"}
										style={{
											fontWeight: "bold",
											fontFamily: "Mukta",
										}}
									>
										Senar: <b>{numberToCurrency(totalSenar, 4)}</b>
									</Flex>
									<Flex
										className="w-full"
										justify={"space-between"}
										style={{
											fontWeight: "bold",
											fontFamily: "Mukta",
										}}
									>
										Total: <b>{numberToCurrency(totalSaldo)}</b>
									</Flex>
								</Card>
							</Flex>
						</Table.Summary.Cell>
					</Table.Summary.Row>
				)}
			/>
		</ImpressaoContainer>
	);
}
